import type { CSSProperties } from 'react';
import { Label, Paper, Rule } from '../design/Paper';
import { ro } from '../i18n/ro';

/**
 * Not part of the app. Corner treatments compared side by side at the sizes
 * they are actually used, because the right answer is not guessable.
 */

const VARIANTS = [
  { key: 'corner-square', name: 'Square', cut: 0, note: 'rule + shadow only' },
  { key: 'corner-chamfer', name: 'Chamfer 4', cut: 4, note: 'subtle bevel' },
  { key: 'corner-chamfer', name: 'Chamfer 8', cut: 8, note: 'current-ish' },
  { key: 'corner-chamfer', name: 'Chamfer 14', cut: 14, note: 'reads as a ticket' },
  { key: 'corner-notch', name: 'Notch 10', cut: 10, note: 'TL + BR only' },
  { key: 'corner-notch', name: 'Notch 16', cut: 16, note: 'TL + BR, strong' },
  { key: 'corner-notch-alt', name: 'Notch alt 12', cut: 12, note: 'TR + BL' },
];

function Sample({
  variant,
  cut,
  tone,
  size,
}: {
  variant: string;
  cut: number;
  tone: 'ink' | 'teal' | 'paper';
  size: 'md' | 'vote';
}) {
  const tones = {
    ink: 'bg-ink text-paper border-ink',
    teal: 'bg-teal text-paper border-ink',
    paper: 'bg-paper-raised text-ink border-ink',
  } as const;
  const sizes = {
    md: 'px-5 py-3 text-sm tracking-[0.14em] border-[3px]',
    vote: 'px-8 py-8 text-3xl tracking-[0.18em] border-4',
  } as const;

  return (
    <button
      type="button"
      className={`pressed corner-shape ${variant}`}
      style={{ '--press': size === 'vote' ? '8px' : '4px' } as CSSProperties}
    >
      <span
        className={`press-face corner-shape flex items-center justify-center font-display uppercase leading-none ${tones[tone]} ${sizes[size]}`}
      >
        {size === 'vote' ? ro.ja : ro.startVote}
      </span>
    </button>
  );
}

export function CornerLab() {
  return (
    <Paper className="min-h-screen" vignette={false}>
      <div className="mx-auto max-w-5xl px-8 py-10">
        <h1 className="font-display text-3xl uppercase">Colțuri — comparație</h1>
        <Rule className="mt-4 mb-8" />

        <div className="space-y-9">
          {VARIANTS.map((v, i) => (
            <div key={i}>
              <div className="mb-3 flex items-baseline gap-3">
                <Label className="text-xs">{v.name}</Label>
                <span className="text-xs text-ink-faint">{v.note}</span>
                <div className="h-px flex-1 bg-ink/15" />
              </div>
              <div className="flex flex-wrap items-center gap-6">
                <Sample variant={v.key} cut={v.cut} tone="ink" size="md" />
                <Sample variant={v.key} cut={v.cut} tone="paper" size="md" />
                <Sample variant={v.key} cut={v.cut} tone="teal" size="vote" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Paper>
  );
}
